import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useDrafts } from '../../domains/drafts/queries'
import { DRAFT_KIND_LABELS } from '../../domains/drafts/types'
import { Button, Card, SectionLabel } from '../../design/components'
import { DraftReviewModal } from './DraftReviewModal'
import styles from './drafts.module.css'

/**
 * The list of changes the assistant is waiting on an answer for.
 *
 * It exists because a draft outlives the conversation that produced it. Bob proposes a skill
 * spread, the user closes the panel to go and look at something, and the proposal has to still be
 * findable an hour later — otherwise the only way to act on one is to never navigate away, which
 * is not how anybody uses the app.
 *
 * Renders nothing when there is nothing pending. A permanently visible empty list is a permanent
 * reminder of an absence.
 */
export function PendingDrafts() {
  const drafts = useDrafts('Pending')
  const [reviewing, setReviewing] = useState<string | null>(null)
  const navigate = useNavigate()

  const pending = drafts.data ?? []
  if (pending.length === 0) return null

  return (
    <>
      <Card>
        <SectionLabel>
          {pending.length === 1 ? 'A change is waiting' : `${pending.length} changes are waiting`}
        </SectionLabel>

        <ul className={styles.changes}>
          {pending.map((draft) => (
            <li key={draft.id} className={styles.change}>
              <span className={styles.changeLabel}>
                {DRAFT_KIND_LABELS[draft.kind ?? 'CreateCharacter']}
                {draft.characterName != null && ` · ${draft.characterName}`}
              </span>
              <span className={styles.changeValue}>
                {draft.summary}
                <Button
                  variant="outline"
                  onClick={() => setReviewing(draft.id ?? null)}
                  aria-label={`Review: ${draft.summary}`}
                >
                  Review
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      {reviewing !== null && (
        <DraftReviewModal
          draftId={reviewing}
          onClose={() => setReviewing(null)}
          // Applying a creation produces a character that did not exist a moment ago, and the
          // only useful next step is to look at it.
          onApplied={(characterId) => void navigate(`/characters/${characterId}`)}
        />
      )}
    </>
  )
}
